import type { PeerMessage } from "@duelgame/shared";
import { ICE_SERVERS } from "../config";

type MessageHandler = (msg: PeerMessage) => void;

export class WebRtcSession {
  private pc: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private handlers = new Set<MessageHandler>();
  private onIceCandidate: ((candidate: RTCIceCandidate) => void) | null = null;
  private onConnectionLost: (() => void) | null = null;
  private openResolve: (() => void) | null = null;
  private openPromise: Promise<void> | null = null;
  private pendingIce: RTCIceCandidateInit[] = [];
  private remoteSet = false;
  /** True while we are tearing down on purpose (leave / reset). */
  private intentionalClose = false;

  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  setIceCandidateHandler(handler: (candidate: RTCIceCandidate) => void): void {
    this.onIceCandidate = handler;
  }

  setConnectionLostHandler(handler: (() => void) | null): void {
    this.onConnectionLost = handler;
  }

  async waitUntilOpen(): Promise<void> {
    if (this.channel?.readyState === "open") return;
    if (!this.openPromise) {
      this.openPromise = new Promise((resolve) => {
        this.openResolve = resolve;
      });
    }
    return this.openPromise;
  }

  private notifyConnectionLost(): void {
    if (this.intentionalClose) return;
    this.onConnectionLost?.();
  }

  private attachChannel(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.binaryType = "arraybuffer";

    channel.onopen = () => {
      this.openResolve?.();
      this.openResolve = null;
    };

    channel.onclose = () => {
      this.notifyConnectionLost();
    };

    channel.onmessage = (ev) => {
      try {
        const msg = JSON.parse(String(ev.data)) as PeerMessage;
        for (const h of this.handlers) h(msg);
      } catch {
        /* ignore */
      }
    };
  }

  private ensurePc(): RTCPeerConnection {
    if (this.pc) return this.pc;
    this.intentionalClose = false;
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.pc = pc;

    pc.onicecandidate = (ev) => {
      if (ev.candidate && this.onIceCandidate) {
        this.onIceCandidate(ev.candidate);
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      if (state === "failed" || state === "closed") {
        this.notifyConnectionLost();
      }
    };

    pc.ondatachannel = (ev) => {
      this.attachChannel(ev.channel);
    };

    return pc;
  }

  private async flushIce(): Promise<void> {
    const pc = this.pc;
    if (!pc || !this.remoteSet) return;
    const pending = this.pendingIce.splice(0, this.pendingIce.length);
    for (const candidate of pending) {
      try {
        await pc.addIceCandidate(candidate);
      } catch {
        /* ignore bad candidates */
      }
    }
  }

  async createOffer(): Promise<string> {
    const pc = this.ensurePc();
    const channel = pc.createDataChannel("duel", { ordered: true });
    this.attachChannel(channel);
    this.openPromise = new Promise((resolve) => {
      this.openResolve = resolve;
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    return JSON.stringify(pc.localDescription);
  }

  async acceptOffer(sdpJson: string): Promise<string> {
    const pc = this.ensurePc();
    this.openPromise = new Promise((resolve) => {
      this.openResolve = resolve;
    });

    const desc = JSON.parse(sdpJson) as RTCSessionDescriptionInit;
    await pc.setRemoteDescription(desc);
    this.remoteSet = true;
    await this.flushIce();
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    return JSON.stringify(pc.localDescription);
  }

  async acceptAnswer(sdpJson: string): Promise<void> {
    const pc = this.ensurePc();
    const desc = JSON.parse(sdpJson) as RTCSessionDescriptionInit;
    await pc.setRemoteDescription(desc);
    this.remoteSet = true;
    await this.flushIce();
  }

  async addIceCandidate(candidateJson: string): Promise<void> {
    this.ensurePc();
    try {
      const candidate = JSON.parse(candidateJson) as RTCIceCandidateInit;
      if (!this.remoteSet) {
        this.pendingIce.push(candidate);
        return;
      }
      await this.pc!.addIceCandidate(candidate);
    } catch {
      /* ignore */
    }
  }

  send(msg: PeerMessage): void {
    if (!this.channel || this.channel.readyState !== "open") {
      throw new Error("Data channel not open");
    }
    this.channel.send(JSON.stringify(msg));
  }

  get ready(): boolean {
    return this.channel?.readyState === "open";
  }

  close(): void {
    this.intentionalClose = true;
    this.channel?.close();
    this.pc?.close();
    this.channel = null;
    this.pc = null;
    this.openPromise = null;
    this.openResolve = null;
    this.pendingIce = [];
    this.remoteSet = false;
  }
}
